import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Ticket, X } from 'lucide-react';
import { usePreviewCoupon } from '../../lib/coupons/use-coupons.js';
import { formatCurrency } from '../../lib/format.js';
import { Button, Input, Label } from '@tailoring-crm/ui';

/**
 * "Enter code" on the booking form.
 *
 * THE FIGURE COMES FROM THE SERVER, never from here. A percentage, its cap, its minimum bill
 * and its usage limits are the shop's rules; a browser that worked the discount out for itself
 * would be a second implementation of those rules, and the day the two disagree is the day a
 * customer is quoted one number and billed another.
 *
 * A CODE THAT DOES NOT WORK IS NOT AN ERROR. It comes back with a named reason and this field
 * turns that into the sentence somebody says to the customer standing in front of them —
 * "that one is for bills over ₹2,000" sends them to add a second garment, where a red "invalid
 * coupon" starts an argument.
 */
export interface CouponFieldProps {
  subtotalPaise: number;
  customerId: string;
  /** The code already applied to this draft, or an empty string. */
  code: string;
  discountPaise: number;
  onApplied: (code: string, discountPaise: number) => void;
}

export function CouponField({
  subtotalPaise,
  customerId,
  code,
  discountPaise,
  onApplied,
}: CouponFieldProps): ReactElement {
  const { t } = useTranslation();
  const preview = usePreviewCoupon();
  const [typed, setTyped] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const apply = (): void => {
    const entered = typed.trim();
    if (!entered) return;
    setRefusal(null);
    void (async () => {
      try {
        const result = await preview.mutateAsync({
          code: entered,
          subtotalPaise,
          ...(customerId ? { customerId } : {}),
        });
        if (!result.usable || result.refusal) {
          setRefusal(
            t(`coupons.refusal.${result.refusal ?? 'unknown'}`, {
              amount: formatCurrency(result.minOrderPaise ?? 0),
            }),
          );
          return;
        }
        onApplied(result.code, result.discountPaise);
        setTyped('');
      } catch {
        setRefusal(t('coupons.refusal.unknown'));
      }
    })();
  };

  if (code) {
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
          <Ticket aria-hidden="true" className="size-4 shrink-0" />
          <span className="truncate font-mono">{code}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="tabular-nums text-muted-foreground">
            −{formatCurrency(discountPaise)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('coupons.remove')}
            onClick={() => onApplied('', 0)}
          >
            <X aria-hidden="true" />
          </Button>
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="order-coupon" className="font-normal text-muted-foreground">
          {t('coupons.enterCode')}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="order-coupon"
            className="h-9 w-32 text-right font-mono uppercase"
            value={typed}
            maxLength={24}
            onChange={(event) => {
              setTyped(event.target.value.toUpperCase());
              setRefusal(null);
            }}
            onKeyDown={(event) => {
              // Enter applies the code rather than submitting the booking — somebody typing a
              // code and pressing Enter means "check this", not "save the order".
              if (event.key === 'Enter') {
                event.preventDefault();
                apply();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={preview.isPending || typed.trim().length === 0}
            onClick={apply}
          >
            {t('coupons.apply')}
          </Button>
        </div>
      </div>
      {refusal ? (
        <p role="alert" className="text-right text-sm text-destructive">
          {refusal}
        </p>
      ) : null}
    </div>
  );
}

export default CouponField;
